<template>
  <v-dialog
    v-model="computedShowModal"
    @click:outside="closeDialog"
    max-width='90vw'
  >
    <v-toolbar dark class='toolbar-settings text-settings' >
      <v-skeleton-loader v-if="loading" type="text" height="20" width="100" class="mb-2"></v-skeleton-loader>
      <v-toolbar-title v-else>{{ $t('campaign.campaignName')}}: {{ campaignData.campaignName }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="closeDialog">
        <md-icon style="color: white;">close</md-icon>
      </v-btn>
    </v-toolbar>
    <v-card style="padding: 15px 20px;">
      <v-spacer></v-spacer>
      <v-card-text style="max-height: 800px; color: unset;" class='px-0 py-0 height-transition'>
        <DataTable
          ref="dataTableTriggerList"
          :title="$t('campaign.listOfTriggeredTriggers')"
          :data='triggerObj.data || []'
          :customTotal='(triggerObj && triggerObj.total) || 0'
          :selectable='false'
          :loading='loading'
          :showPagination='false'
          customHeader
          :buttonHeaders="false"
          :endpoint='endpoint'
          :labels='labels'
          :filter='filter'
          :formatLabelFunction='formatLabels'
          fileName='triggeredList'
          class="datable"
          :enablePDF="false"
          :currentSortOrder.sync='filter.order'
        >
        <template slot="custom-filter">
          <VueJsonToCsv
            :json-data="triggerObj.data" :labels="csvLabels" :csv-title='`triggeredList ${campaignData.campaignName}`'
          >
            <md-button
              class="margin-left margin-top md-sm md-facebook"
              :disabled="triggerObj.total === 0"
              style="float: right;"
            >
              <md-icon style="font-size: 24px !important;">table_view</md-icon>
              <md-tooltip md-direction="bottom">
                {{ $t('dataTable.generateSpreadsheet') }}
              </md-tooltip>
            </md-button>
          </VueJsonToCsv>
          </template>
          <template slot="no-data" v-if="triggerObj.total === 0 && !loading">
            <md-empty-state
              md-icon="info"
              :md-label="$t('dataTable.notEnougConsolidatedData')"
            />
          </template>
          <template slot="cells" slot-scope="{ item }">
            <md-table-cell :md-label='labels[0]'>{{ formatLabels(item, 0) }}</md-table-cell>
            <md-table-cell :md-label='labels[1]'>{{ formatLabels(item, 1) }}</md-table-cell>
            <md-table-cell :md-label='labels[2]'>{{ formatLabels(item, 2) }}</md-table-cell>
            <md-table-cell :md-label='labels[3]'>{{ formatLabels(item, 3) }}</md-table-cell>
            <md-table-cell :md-label='labels[4]'>{{ formatLabels(item, 4) }}</md-table-cell>
            <md-table-cell :md-label='labels[5]'>{{ formatLabels(item, 5) }}</md-table-cell>
            <md-table-cell :md-label='labels[6]'>{{ formatLabels(item, 6) }}</md-table-cell>
            <md-table-cell :md-label='labels[7]'>{{ formatLabels(item, 7) }}</md-table-cell>
            <md-table-cell :md-label='labels[8]'>{{ formatLabels(item, 8) }}</md-table-cell>
          </template>
        </DataTable>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { selectedLocale } from '@/plugins/i18n'
import moment from 'moment'
import Swal from 'sweetalert2'
import { v4 as uuidv4 } from 'uuid'
import DataTable from './DataTable.vue'
import VueJsonToCsv from 'vue-json-to-csv'

export default {
  name: 'TriggeredNotification',
  props: {
    showTriggerNotificationDialog: {
      type: Boolean
    },
    campaignData: {
      type: Object,
      default: () => {}
    }
  },
  components: {
    DataTable,
    VueJsonToCsv
  },
  data () {
    return {
      loading: false,
      loadingCsv: false,
      triggerObj: {
        data: [],
        total: 0
      },
      labels: [
        this.$t('search.start'),
        this.$t('chatTable.end'),
        this.$t('campaign.triggerDuration'),
        this.$t('campaign.campaignAllSent'),
        this.$t('campaign.noReturn'),
        this.$t('campaign.campaignDelivered'),
        this.$t('campaign.campaignAllRead'),
        this.$t('campaign.statusFailed'),
        this.$t('campaign.conversationsStarted')
      ],
      csvLabels: {},
      filter: {
        order: 'desc',
        page: 0,
        perPage: 5
      }
    }
  },
  created () {
    this.getTriggeredListNotification(this.campaignData.campaignId)
    this.csvLabels = {
      startDate: { title: this.labels[0] },
      endDate: { title: this.labels[1] },
      diffInTime: { title: this.labels[2] },
      notificationsSent: { title: this.labels[3] },
      notificationNoReturn: { title: this.labels[4] },
      notificationsDelivered: { title: this.labels[5] },
      notificationsRead: { title: this.labels[6] },
      notificationsFailed: { title: this.labels[7] },
      notificationsProactive: { title: this.labels[8] }
    }
  },
  computed: {
    endpoint () {
      return `${process.env.VUE_APP_BACKEND_BASE_URL}/campaign/triggerNotification?campaignId=${this.campaignData.campaignId}`
    },
    computedShowModal: {
      get () {
        return this.showTriggerNotificationDialog
      },
      set (value) {
        if (!value) this.closeDialog()
      }
    }
  },
  methods: {
    formatDate (date) {
      return moment(date).locale(selectedLocale).format('L HH:mm:ss')
    },
    diffInDateTime (startDate, endDate) {
      const diffInMilliseconds = moment(endDate).diff(moment(startDate))

      const hours = Math.floor(diffInMilliseconds / 3600000)
      const minutes = Math.floor((diffInMilliseconds % 3600000) / 60000)
      let seconds = Math.floor((diffInMilliseconds % 60000) / 1000)

      if (diffInMilliseconds > 0 && diffInMilliseconds < 1000) {
        seconds = 1
      }

      return `${hours}h ${minutes}m ${seconds}s`
    },
    formatLabels (item, index) {
      switch (index) {
        case 0:
          return item.startDate
        case 1:
          return item.endDate
        case 2:
          return item.diffInTime
        case 3:
          return item.notificationsSent
        case 4:
          return item.notificationNoReturn
        case 5:
          return item.notificationsDelivered
        case 6:
          return item.notificationsRead
        case 7:
          return item.notificationsFailed
        case 8:
          return item.notificationsProactive
        default:
          break
      }
    },
    formatTriggerDataData (triggerArr) {
      return triggerArr.map(trigger => {
        return {
          ...trigger,
          id: uuidv4(),
          startDate: this.formatDate(trigger.startDate),
          endDate: this.formatDate(trigger.endDate),
          diffInTime: this.diffInDateTime(trigger.startDate, trigger.endDate),
          notificationNoReturn: Number(trigger.notificationsSent) - Number(trigger.notificationsFailed) - Number(trigger.notificationsDelivered)
        }
      })
    },
    async getTriggeredListNotification (campaignId) {
      try {
        this.loading = true
        const { data } = await this.$axios(`${process.env.VUE_APP_REPORTS_BACKEND}/campaign/triggerNotification?campaignId=${campaignId}`)
        if (data.total > 0) {
          this.triggerObj = {
            data: this.formatTriggerDataData(data.data),
            total: data.total
          }
        }
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: this.$t('testNotificationDialog.thereWasAnErrorInYourRequest'),
          text: error
        })
        this.closeDialog()
      } finally {
        this.loading = false
      }
    },
    closeDialog () {
      this.$emit('closeTriggerNotificationDialog')
    }
  }
}
</script>
<style lang="scss" scoped>
::v-deep {
  table {
    table-layout: fixed;
    @media screen and (max-width: 900px) {
      table-layout: auto;
    }
  }
}
.v-dialog__content {
  ::-webkit-scrollbar {
    width: 10px;
  }
  ::-webkit-scrollbar-track {
    border-radius: 3.5px;
    background-color: #fff;
  }
  ::-webkit-scrollbar-thumb {
    border-radius: 3.5px;
    border: 2px solid #fff;
    background-color: rgba(0, 0, 0, 0.5);
    box-sizing: border-box;
  }
}

</style>
